import {
  Action,
  Card,
  Modal,
  NumberInput,
  SimpleSelect,
  SwitchInput,
  TextInput,
  UrlInput,
  Disclosure,
  SimpleSummary,
} from 'corso-admin-ui';
import { Pegasus } from 'corso-types';
import { useEffect, useState } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import DescriptionList from '~/components/DescriptionList';
import { useAutomationFormContext } from '~/features/settings/automations/hooks/useAutomationForm';
import {
  CUSTOM_PARCEL,
  findPredefinedParcelByDimensions,
  parcelSelectOptions,
  predefinedParcels,
} from './predefinedParcels';

type ParcelDraft =
  Pegasus.EventCustomizeReturnShipment['params']['returnParcels'][number];

const emptyDraft: ParcelDraft = {
  name: '',
  length: CUSTOM_PARCEL.length,
  width: CUSTOM_PARCEL.width,
  height: CUSTOM_PARCEL.height,
  weight: 1,
};

const isDraftValid = (draft: ParcelDraft) =>
  Pegasus.customReturnParcel.safeParse(draft).success;

function ParcelModal({
  open,
  initial,
  title,
  onSave,
  onClose,
}: {
  open: boolean;
  initial: ParcelDraft;
  title: string;
  onSave: (draft: ParcelDraft) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<ParcelDraft>(initial);

  useEffect(() => {
    if (open) setDraft(initial);
  }, [open, initial]);

  const predefinedMatch = findPredefinedParcelByDimensions(draft);

  const selectedType = predefinedMatch ?? CUSTOM_PARCEL;

  const handleSetType = (parcelName: Pegasus.Parcel['name']) => {
    const found = predefinedParcels.find((p) => p.name === parcelName);
    if (!found) return;
    setDraft((prev) => ({
      ...prev,
      length: found.length,
      width: found.width,
      height: found.height,
    }));
  };

  return (
    <Modal
      show={open}
      onClose={onClose}
      title={title}
      size="lg"
      actions={
        <>
          <Action onClick={onClose}>Cancel</Action>
          <Action
            variant="primary"
            disabled={!isDraftValid(draft)}
            onClick={() => onSave(draft)}
          >
            Save
          </Action>
        </>
      }
    >
      <SimpleSelect
        options={parcelSelectOptions}
        label="Type"
        value={selectedType.name}
        defaultValue={selectedType.name}
        onChange={handleSetType}
      />

      <TextInput
        id="parcel-modal-name"
        label="Name"
        details="Optionally provide a name to indicate the contents of the parcel, e.g. 'Table Top'."
        value={draft.name}
        onChange={(e) =>
          setDraft((prev) => ({ ...prev, name: e.target.value }))
        }
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {!predefinedMatch && (
          <>
            <NumberInput
              id="parcel-modal-length"
              required
              step={0.01}
              label="Length"
              value={draft.length}
              addon={{ insideEnd: 'in' }}
              onChange={(e) =>
                setDraft((prev) => ({
                  ...prev,
                  length: e.target.valueAsNumber,
                }))
              }
            />
            <NumberInput
              id="parcel-modal-width"
              required
              step={0.01}
              label="Width"
              value={draft.width}
              addon={{ insideEnd: 'in' }}
              onChange={(e) =>
                setDraft((prev) => ({
                  ...prev,
                  width: e.target.valueAsNumber,
                }))
              }
            />
            <NumberInput
              id="parcel-modal-height"
              required
              step={0.01}
              label="Height"
              value={draft.height}
              addon={{ insideEnd: 'in' }}
              onChange={(e) =>
                setDraft((prev) => ({
                  ...prev,
                  height: e.target.valueAsNumber,
                }))
              }
            />

            <NumberInput
              id="parcel-modal-weight"
              step={0.01}
              min={0.1}
              label="Weight"
              value={draft.weight}
              addon={{ insideEnd: 'oz' }}
              onChange={(e) =>
                setDraft((prev) => ({
                  ...prev,
                  weight: e.target.valueAsNumber,
                }))
              }
            />
          </>
        )}
      </div>
    </Modal>
  );
}

function ParcelSummary({
  parcel,
  canRemove,
  onEdit,
  onRemove,
}: {
  parcel: ParcelDraft;
  canRemove: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const displayName = parcel.name?.trim() || 'Parcel';
  return (
    <Disclosure
      renderSummary={
        <SimpleSummary>
          <div className="flex w-full flex-row items-center justify-between gap-2">
            <p className="text-corso-gray-700 truncate text-left text-sm font-medium">
              {displayName}
            </p>
            <div className="flex items-center gap-1">
              <Action
                variant="ghost"
                icon="edit"
                accessibilityLabel={`Edit ${displayName}`}
                onClick={(e) => {
                  e.preventDefault();
                  onEdit();
                }}
              />
              {canRemove && (
                <Action
                  variant="ghost"
                  icon="remove"
                  accessibilityLabel={`Remove ${displayName}`}
                  onClick={(e) => {
                    e.preventDefault();
                    onRemove();
                  }}
                />
              )}
            </div>
          </div>
        </SimpleSummary>
      }
    >
      <div className="mt-1 px-2">
        <DescriptionList
          descriptions={[
            {
              term: 'Dimensions',
              details: `${parcel.length} x ${parcel.width} x ${parcel.height} in`,
            },
            { term: 'Weight', details: `${parcel.weight ?? '—'} oz` },
          ]}
        />
      </div>
    </Disclosure>
  );
}

type ModalState =
  | { kind: 'closed' }
  | { kind: 'add' }
  | { kind: 'edit'; index: number; initial: ParcelDraft };

export function EventCustomizeReturnShipment() {
  const { control, getValues } = useAutomationFormContext();

  const {
    fields: parcelFields,
    append: addParcel,
    update: updateParcel,
    remove: removeParcel,
  } = useFieldArray({
    control,
    name: 'event.params.returnParcels',
  });

  const [modal, setModal] = useState<ModalState>({ kind: 'closed' });

  const closeModal = () => setModal({ kind: 'closed' });

  const handleSave = (draft: ParcelDraft) => {
    if (modal.kind === 'add') addParcel(draft);
    else if (modal.kind === 'edit') updateParcel(modal.index, draft);
    closeModal();
  };

  return (
    <div className="flex flex-col gap-4">
      <Controller
        control={control}
        name="event.params.isApplicablePerLineItemQuantity"
        render={({ field: { onChange, value } }) => (
          <SwitchInput
            id="customize-return-shipment-per-quantity"
            label="Apply per item quantity"
            details="When enabled, each unit of an item's quantity generates its own set of parcels."
            checked={value ?? true}
            onChange={onChange}
          />
        )}
      />

      <Card.Line>
        <Card.Heading>Parcels</Card.Heading>
        <Action
          variant="ghost"
          icon="add"
          accessibilityLabel="Add Parcel"
          onClick={() => setModal({ kind: 'add' })}
        />
      </Card.Line>

      {parcelFields.map((field, index) => {
        const parcel = getValues(`event.params.returnParcels.${index}`);
        return (
          <ParcelSummary
            key={field.id}
            parcel={parcel}
            canRemove={parcelFields.length > 1}
            onEdit={() => setModal({ kind: 'edit', index, initial: parcel })}
            onRemove={() => removeParcel(index)}
          />
        );
      })}

      <Card.Heading>Packing Guide</Card.Heading>
      <Controller
        control={control}
        name="event.params.fileUrl"
        render={({ field, fieldState }) => (
          <UrlInput
            label="File URL"
            details="Optional link to a packaging guide or documentation."
            id="event-file-url"
            error={fieldState.error?.message}
            value={field.value ?? ''}
            onChange={(e) => field.onChange(e.target.value || undefined)}
          />
        )}
      />

      <Controller
        control={control}
        name="event.params.fileUrlCta"
        render={({ field, fieldState }) => (
          <TextInput
            label="File URL CTA"
            details="The call-to-action text shown for the file link, e.g. 'Download packing guide'."
            id="event-file-url-cta"
            error={fieldState.error?.message}
            value={field.value ?? ''}
            onChange={(e) => field.onChange(e.target.value || undefined)}
          />
        )}
      />

      <ParcelModal
        open={modal.kind !== 'closed'}
        title={modal.kind === 'edit' ? 'Edit Parcel' : 'Add Parcel'}
        initial={modal.kind === 'edit' ? modal.initial : emptyDraft}
        onSave={handleSave}
        onClose={closeModal}
      />
    </div>
  );
}
